import {
  ExcalidrawElement,
  NonDeletedExcalidrawElement,
} from "../element/types";
import { getElementAbsoluteCoords, getElementBounds } from "../element";
import { AppState, InteractiveCanvasAppState } from "../types";
import {isBoundToContainer, isFrameLikeElement, isMindmapElement} from "../element/typeChecks";
import {
  elementOverlapsWithFrame,
  getContainingFrame,
  getFrameChildren,
} from "../frame";
import { isShallowEqual } from "../utils";
import { isElementInViewport } from "../element/sizeHelpers";
import {HAS_ENABLE_TIPTAP_TEXT} from "../../../../src/Const";

/**
 * Frames and their containing elements are not to be selected at the same time.
 * Given an array of selected elements, if there are frames and their containing elements
 * we only keep the frames.
 * @param selectedElements
 */
export const excludeElementsInFramesFromSelection = <
  T extends ExcalidrawElement,
>(
  selectedElements: readonly T[],
) => {
  const framesInSelection = new Set<T["id"]>();

  selectedElements.forEach((element) => {
    if (isFrameLikeElement(element)) {
      framesInSelection.add(element.id);
    }
  });

  return selectedElements.filter((element) => {
    if (element.frameId && framesInSelection.has(element.frameId)) {
      return false;
    }
    return true;
  });
};

// 一个点的坐标是否在矩形内
const isPointInRectangle = (
    x: number,
    y: number,
    elementX1: number,
    elementY1: number,
    elementX2: number,
    elementY2: number,
) => {
  const minX = Math.min(elementX1, elementX2);
  const maxX = Math.max(elementX1, elementX2);
  const minY = Math.min(elementY1, elementY2);
  const maxY = Math.max(elementY1, elementY2);

  return x >= minX && x <= maxX && y >= minY && y <= maxY;
};
// 多个点，任意一个在矩形内部
const isAnyPointInRectangle = (
    points: number[][],
    elementX1: number,
    elementY1: number,
    elementX2: number,
    elementY2: number,
) => {
  for (let i = 0; i < points.length; i++) {
    const [x, y] = points[i];
    if (isPointInRectangle(x, y, elementX1, elementY1, elementX2, elementY2)) {
      return true; // 如果任意一个点在矩形内部，返回true
    }
  }
  return false; // 如果所有点都不在矩形内部，返回false
};

export const getElementsWithinSelection = (
  elements: readonly NonDeletedExcalidrawElement[],
  selection: NonDeletedExcalidrawElement,
  excludeElementsInFrames: boolean = false,
) => {
  const [selectionX1, selectionY1, selectionX2, selectionY2] =
    getElementAbsoluteCoords(selection);

  let elementsInSelection = elements.filter((element) => {
    let [elementX1, elementY1, elementX2, elementY2] =
      getElementBounds(element);

    const containingFrame = getContainingFrame(element);
    if (containingFrame) {
      const [fx1, fy1, fx2, fy2] = getElementBounds(containingFrame);

      elementX1 = Math.max(fx1, elementX1);
      elementY1 = Math.max(fy1, elementY1);
      elementX2 = Math.min(fx2, elementX2);
      elementY2 = Math.min(fy2, elementY2);
    }

    if (
        element.locked === true ||
        element.type === "selection" ||
        (isBoundToContainer(element) && !HAS_ENABLE_TIPTAP_TEXT)
    ) {
      return false;
    }

    if (isFrameLikeElement(element) || isMindmapElement(element)) {
      return (
          selectionX1 <= elementX1 &&
          selectionY1 <= elementY1 &&
          selectionX2 >= elementX2 &&
          selectionY2 >= elementY2
      );
    }

    return (
        // 手拉动的矩形和元素相交
        isAnyPointInRectangle(
            [
              [selectionX1, selectionY1],
              [selectionX2, selectionY1],
              [selectionX2, selectionY2],
              [selectionX1, selectionY2],
            ],
            elementX1,
            elementY1,
            elementX2,
            elementY2,
        ) || // 手拉动的矩形包含元素
        isAnyPointInRectangle(
            [
              [elementX1, elementY1],
              [elementX2, elementY1],
              [elementX2, elementY2],
              [elementX1, elementY2],
            ],
            selectionX1,
            selectionY1,
            selectionX2,
            selectionY2,
        )
    );
  });

  elementsInSelection = excludeElementsInFrames
    ? excludeElementsInFramesFromSelection(elementsInSelection)
    : elementsInSelection;

  elementsInSelection = elementsInSelection.filter((element) => {
    const containingFrame = getContainingFrame(element);

    if (containingFrame) {
      return elementOverlapsWithFrame(element, containingFrame);
    }

    return true;
  });

  // if (
  //     elementsInSelection.length > 1 &&
  //     elementsInSelection.some((element) => element.type === "frame")
  // ) {
  //   elementsInSelection = elementsInSelection.filter(
  //       (element) => element.type !== "frame",
  //   );
  // }


    // 下面这个排除了多选时候的arrow的元素-- 连接箭头不加到frame里面，frame预览的时候箭头就不见了....
  let linesInSelection: any = [];
  elementsInSelection.forEach((element) => {
    if (element.startConnectionDirection) {
      linesInSelection.push(...Object.keys(element.startConnectionDirection));
    }
    if (element.endConnectionDirection) {
      linesInSelection.push(...Object.keys(element.endConnectionDirection));
    }
  });
  linesInSelection = [...new Set(linesInSelection)];
  elementsInSelection = elementsInSelection.filter((element) => {
    return !linesInSelection.includes(element.id);
  });


  return elementsInSelection;
};

export const getVisibleAndNonSelectedElements = (
  elements: readonly NonDeletedExcalidrawElement[],
  selectedElements: readonly NonDeletedExcalidrawElement[],
  appState: AppState,
) => {
  const selectedElementsSet = new Set(
    selectedElements.map((element) => element.id),
  );
  return elements.filter((element) => {
    const isVisible = isElementInViewport(
      element,
      appState.width,
      appState.height,
      appState,
    );

    return !selectedElementsSet.has(element.id) && isVisible;
  });
};

// FIXME move this into the editor instance to keep utility methods stateless
export const isSomeElementSelected = (function () {
  let lastElements: readonly NonDeletedExcalidrawElement[] | null = null;
  let lastSelectedElementIds: AppState["selectedElementIds"] | null = null;
  let isSelected: boolean | null = null;

  const ret = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: Pick<AppState, "selectedElementIds">,
  ): boolean => {
    if (
      isSelected != null &&
      elements === lastElements &&
      appState.selectedElementIds === lastSelectedElementIds
    ) {
      return isSelected;
    }

    isSelected = elements.some(
      (element) => appState.selectedElementIds[element.id],
    );
    lastElements = elements;
    lastSelectedElementIds = appState.selectedElementIds;

    return isSelected;
  };

  ret.clearCache = () => {
    lastElements = null;
    lastSelectedElementIds = null;
    isSelected = null;
  };

  return ret;
})();

/**
 * Returns common attribute (picked by `getAttribute` callback) of selected
 *  elements. If elements don't share the same value, returns `null`.
 */
export const getCommonAttributeOfSelectedElements = <T>(
  elements: readonly NonDeletedExcalidrawElement[],
  appState: Pick<AppState, "selectedElementIds">,
  getAttribute: (element: ExcalidrawElement) => T,
): T | null => {
  const attributes = Array.from(
    new Set(
      getSelectedElements(elements, appState).map((element) =>
        getAttribute(element),
      ),
    ),
  );
  return attributes.length === 1 ? attributes[0] : null;
};

export const getSelectedElements = (
  elements: readonly NonDeletedExcalidrawElement[],
  appState: Pick<InteractiveCanvasAppState, "selectedElementIds">,
  opts?: {
    includeBoundTextElement?: boolean;
    includeElementsInFrames?: boolean;
  },
) => {
  const selectedElements = elements.filter((element) => {
    if (!appState.selectedElementIds || !element) {
      return
    };
    if (appState.selectedElementIds[element.id]) {
      return element;
    }
    if (
      opts?.includeBoundTextElement &&
      isBoundToContainer(element) &&
      appState.selectedElementIds[element?.containerId]
    ) {
      return element;
    }
    return null;
  });

  if (opts?.includeElementsInFrames) {
    const elementsToInclude: ExcalidrawElement[] = [];
    selectedElements.forEach((element) => {
      if (isFrameLikeElement(element)) {
        getFrameChildren(elements, element.id).forEach((e) =>
          elementsToInclude.push(e),
        );
      }
      elementsToInclude.push(element);
    });

    return elementsToInclude;
  }

  return selectedElements;
};

export const getTargetElements = (
  elements: readonly NonDeletedExcalidrawElement[],
  appState: Pick<AppState, "selectedElementIds" | "editingElement">,
) =>
  appState.editingElement
    ? [appState.editingElement]
    : getSelectedElements(elements, appState, {
        includeBoundTextElement: true,
      });

/**
 * returns prevState's selectedElementids if no change from previous, so as to
 * retain reference identity for memoization
 */
export const makeNextSelectedElementIds = (
  nextSelectedElementIds: AppState["selectedElementIds"],
  prevState: Pick<AppState, "selectedElementIds">,
) => {
  if (isShallowEqual(prevState.selectedElementIds, nextSelectedElementIds)) {
    return prevState.selectedElementIds;
  }

  return nextSelectedElementIds;
};
